<template>
  <div class="mine-control">
    <div class="control-wrap">
      <p class="label">{{ $t('mine.control.type.title') }}</p>
      <div class="type-wrap">
        <div class="type-selector" @click.stop="toggleTypeList">
          <p :class="['type-selected', { 'some': !isAllTypeSelected }]">
            <template v-if="isAllTypeSelected">{{ $t('common.all') }}</template>
            <template v-else-if="selectedTypes.length === 1">{{ typeItems.find(item => item.value === selectedTypes[0]).text }}</template>
            <template v-else>{{ selectedTypes.length }}</template>
          </p>
        </div>
        <ul class="type-list" v-show="showTypeList" @click.stop>
          <li :class="['type-list-item', {'all': !item.value && isAllTypeSelected}]" v-for="item in typeItems" :key="item.value" @click="selectType(item)">
            <ui-checkbox v-if="item.value === null"
              :value="isAllTypeSelected"
              :unselect="isAllTypeSelected || selectedTypes.length"
            />
            <ui-checkbox v-else :value="checkIsSelected(item) || isAllTypeSelected"/>
            <p>{{ item.text }}</p>
            <i class="tooltip icon icon--info-outline-gray" v-if="item.tooltip" v-btf-tooltip:bottom="{contents: item.tooltip , size: 220, offsetY: -5, textAlign: 'left'}"></i>
          </li>
        </ul>
      </div>
    </div>
    <div class="control-wrap">
      <p class="label">{{ $t('mine.control.createdDate.title') }}</p>
      <ui-select-box :width="128"
                    :dropWidth="128"
                    :items="regDateRangeItems"
                    :value="regDateRange"
                    :hideLabel="true"
                    @input="onRegDateRangeChange" />
    </div>
    <div class="control-wrap">
      <p class="label">{{ $t('mine.control.analysisPeriod') }}</p>
      <div class="date-wrap">
        <i class="icon icon--calendar-input"></i>
        <input type="text"
                :value="startDateStr"
                :placeholder="'YYYY-MM-DD'"
                @click.stop.prevent="toggleDatePicker('start')"
                readonly>
      </div>
      <span>-</span>
      <div class="date-wrap">
        <i class="icon icon--calendar-input"></i>
        <input type="text"
                :value="endDateStr"
                :placeholder="'YYYY-MM-DD'"
                @click.stop.prevent="toggleDatePicker('end')"
                readonly>
      </div>
      <date-picker v-if="activeDatePicker"
                  :class="[activeDatePicker, activeDatePicker === 'start' ? 'data__start-date' : 'data__end-date']"
                  :value="activeDatePicker === 'start' ? startDate : endDate"
                  :isShowTimezone="true"
                  :isShowTimezoneValue="true"
                  :minDate="calendarMinDate"
                  :maxDate="calendarMaxDate"
                  :timezone="timezone"
                  :timezoneName="timezoneName"
                  :isYearMove="true"
                  @input="onCalendarDateChange(activeDatePicker === 'start' ? 'startDate' : 'endDate', $event)"
                  @onCloseClick="activeDatePicker = null"/>
      <button class="reset" @click="initFilter">
        <i class="icon icon--reset-large" />
      </button>
    </div>
  </div>
</template>

<script>
import { DEFAULT_TIMEZONE, DEFAULT_TIMEZONE_NAME } from '@/libs/constants'
import { endOfDay, format, parse, startOfDay } from 'date-fns'
import { EventBus } from '@/libs/event-bus'
import UiSelectBox from '@/components/ui/UiSelectbox'
import UiCheckbox from '@/components/ui/UiCheckbox'
import DatePicker from '@/components/date-picker/index.vue'

export default {
  name: 'MineControl',
  components: {
    UiSelectBox,
    UiCheckbox,
    DatePicker
  },
  data () {
    return {
      selectedTypes: ['dashboard', 'dashboardHell', 'journey', 'ranking', 'trend', 'cxTrends', 'mashup'],
      regDateRange: null,
      showTypeList: false,
      activeDatePicker: null,
      startDate: null,
      endDate: null,
      startDateStr: null,
      endDateStr: null
    }
  },
  computed: {
    typeItems () {
      return [
        { value: null, text: this.$t('common.all') },
        { value: 'dashboard', text: 'Dashboard: CX' },
        { value: 'dashboardHell', text: 'Dashboard: Hell' },
        { value: 'journey', text: 'Journey' },
        { value: 'ranking', text: 'Ranking' },
        { value: 'trend', text: 'Trend' },
        { value: 'cxTrends', text: 'CX Data Trend' },
        { value: 'mashup', text: 'Etc.', tooltip: this.$t('mine.control.type.etcTooltip') }
      ]
    },
    regDateRangeItems () {
      return [
        { value: null, text: this.$t('common.all') },
        { value: 0, text: this.$t('mine.control.createdDate.today') },
        { value: -7, text: this.$t('mine.control.createdDate.recentDays', { days: 7 }) },
        { value: -30, text: this.$t('mine.control.createdDate.recentDays', { days: 30 }) },
        { value: this.getDaysDiffFromMonths(3), text: this.$t('mine.control.createdDate.recentMonths', { months: 3 }) },
        { value: this.getDaysDiffFromMonths(6), text: this.$t('mine.control.createdDate.recentMonths', { months: 6 }) }
      ]
    },
    timezone () {
      return DEFAULT_TIMEZONE
    },
    timezoneName () {
      return DEFAULT_TIMEZONE_NAME
    },
    calendarMinDate () {
      if (this.activeDatePicker === 'end' && this.startDateStr) {
        return startOfDay(parse(this.startDateStr))
      }
      return null
    },
    calendarMaxDate () {
      const todayEnd = endOfDay(new Date())
      if (this.activeDatePicker === 'start' && this.endDateStr) {
        const endDate = endOfDay(parse(this.endDateStr))
        return endDate < todayEnd ? endDate : todayEnd
      }
      return todayEnd
    },
    isAllTypeSelected () {
      return this.selectedTypes.length === this.typeItems.filter(item => item.value !== null).length
    }
  },
  methods: {
    selectAll () {
      if (this.isAllTypeSelected) {
        this.selectedTypes = []
      } else {
        this.selectedTypes = this.typeItems.filter(item => item.value !== null).map(item => item.value)
      }
    },
    toggleTypeList () {
      this.showTypeList = !this.showTypeList
      if (this.showTypeList) {
        document.addEventListener('click', this.closeTypeList)
      }
    },
    closeTypeList () {
      this.showTypeList = false
      document.removeEventListener('click', this.closeTypeList)
    },
    checkIsSelected (item) {
      return this.selectedTypes.includes(item.value)
    },
    selectType (item) {
      if (item.value === null) {
        this.selectAll()
      } else if (this.selectedTypes.includes(item.value)) {
        this.selectedTypes = this.selectedTypes.filter(type => type !== item.value)
      } else {
        this.selectedTypes = [...this.selectedTypes, item.value]
      }
      this.emitFilterChange()
    },
    toggleDatePicker (type) {
      if (this.activeDatePicker && this.activeDatePicker !== type) {
        this.activeDatePicker = null
        this.$nextTick(() => {
          this.activeDatePicker = type
        })
        return
      }
      this.activeDatePicker = this.activeDatePicker === type ? null : type
    },
    onRegDateRangeChange (value) {
      this.regDateRange = value
      this.emitFilterChange()
    },
    initFilter () {
      this.selectedTypes = this.typeItems.filter(item => item.value !== null).map(item => item.value)
      this.regDateRange = null
      this.startDate = null
      this.endDate = null
      this.startDateStr = null
      this.endDateStr = null
      this.emitFilterChange()
    },
    onCalendarDateChange (dateType, value) {
      const isStart = dateType === 'startDate'
      const dateValue = isStart ? startOfDay(parse(value)) : endOfDay(parse(value))
      const serverDateStr = format(dateValue, 'YYYY-MM-DD')

      if (isStart) {
        this.startDateStr = value
        this.startDate = serverDateStr
      } else {
        this.endDateStr = value
        this.endDate = serverDateStr
      }
      this.emitFilterChange()
    },
    emitFilterChange () {
      const payload = {
        typeList: this.isAllTypeSelected ? null : this.selectedTypes,
        regDateRange: this.regDateRange,
        startDate: this.startDate,
        endDate: this.endDate
      }
      EventBus.$emit('mineFilterChange', payload)
    },
    getDaysDiffFromMonths (months) {
      const today = new Date()
      const past = new Date(today)
      past.setMonth(today.getMonth() - months)

      return Math.floor((past - today) / (1000 * 60 * 60 * 24))
    }
  },
  beforeDestroy () {
    document.removeEventListener('click', this.closeTypeList)
  },
  watch: {
  }
}
</script>

<style lang="scss" scoped>
.mine-control {
  display: flex;
  align-items: center;
  gap: 17px;
  margin-top: 10px;
  transition: transform 0.3s ease;

  .control-wrap {
    position: relative;
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: 14px;
    &:not(:last-child) {
      &:after {
        content: '';
        position: absolute;
        top: 50%;
        right: -9px;
        transform: translateY(-50%);
        width: 1px;
        height: 16px;
        background-color: #E0E0E0;
      }
    }

    .label {
      color: #767676;
      font-size: 13px;
      font-weight: 600;
      line-height: 100%;
    }
    .type-wrap {
      position: relative;
      color: #2F2F2F;
      cursor: pointer;
      .type-selector {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 188px;
        height: 36px;
        padding: 8px 9px;
        border-radius: 6px;
        border: 1px solid #BBB;
        background: #FFF;
        box-sizing: border-box;
        .type-selected {
          font-size: 13px;
          font-weight: 400;
          line-height: 100%;
          &.some {
            padding: 4px 8px;
            border-radius: 999px;
            background: $brandColor;
            color: #FFF;
          }
        }
      }
      .type-list {
        position: absolute;
        top: 41px;
        left: 0;
        width: 188px;
        border-radius: 5px;
        border: 1px solid #BBB;
        background-color: #FFF;
        box-shadow: 0 20px 16px -12px rgba(0, 0, 0, 0.12);
        box-sizing: border-box;
        &-item {
          display: flex;
          height: 36px;
          padding: 0 10px;
          align-items: center;
          gap: 8px;
          box-sizing: border-box;
          cursor: pointer;
          font-size: 14px;
          .ui_checkbox {
            margin-left: 0;
            & /deep/ .text {
              margin-left: 16px;
            }
          }
          &.all {
            font-weight: 600;
            background-color: #F4F4F4;
          }
          .tooltip {
            margin-left: -4px;
          }
          &:hover {
            background-color: #EBEBEB;
          }
          &:first-child {
            border-bottom: 1px solid #E0E0E0;
            border-top-left-radius: 5px;
            border-top-right-radius: 5px;
          }
          &:last-child {
            border-bottom-left-radius: 5px;
            border-bottom-right-radius: 5px;
          }
        }
      }
    }
    .ui_select {
      cursor: pointer;
      & /deep/ {
        .item--wrap {
          height: 36px;
          padding-left: 10px;
          font-size: 13px;
          .item__list {
            top: 39px;
            &-item {
              padding-left: 9px;
              color: #2F2F2F;
              &.on {
                font-weight: 600;
              }
            }
          }
        }
      }
    }
    .date-wrap {
      position: relative;
      width: 152px;
      height: 38px;
      cursor: pointer;
      input {
        text-indent: 35px;
        display: inline-block;
        width: 100%;
        height: 100%;
        border-radius: 5px;
        border: solid 1px #bbbbbb;
        background-color: #ffffff;
        box-sizing: border-box;
        font-size: 14px;
        color: #999999;
        cursor: pointer;
      }
      .icon--calendar-input {
        position: absolute;
        top: 50%;
        left: 9px;
        transform: translateY(-50%);
      }
    }

    .date_picker {
      top: 43px;
      left: -16px;
      transform: none;
      &.end {
        left: 157px;
      }
    }

    .reset {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      border-radius: 999px;
      &:hover {
        background: rgba(0, 0, 0, 0.06);
      }
    }
  }
}
</style>
